import { Container, SettingContainer, TitleContainer } from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import { useToast } from "hook/useToast";
import { useEffect, useState } from "react";
import {
  getTabletPolicy,
  updateAutoCheckOut,
  updateAutoCheckOutDetectionTime,
  updateAutoCheckOutSoundAlert,
  updateMeetingFinishAlert,
  updateMeetingFinishAlertTime,
  updateMeetingFinishSoundAlert,
  updateNoShowWarningSoundAlert,
  updateTabletAutoCheckIn,
} from "apis/meeting-settings/tablet";
import { CheckInPolicySetting, CheckOutPolicySetting, PreMeetingEndAlertSetting } from "uis/PolicyManage";
import { GLOBAL_MEETING_SETTING_VALUE } from "types/meeting-settings.types";

const SettingTabletAlarm = () => {
  const { showToast } = useToast();
  const [hasMeetingRoomUsedTablet, setHasMeetingRoomUsedTablet] = useState<boolean>(false);

  const [noShowEnabled, setNoshowEnabled] = useState<GLOBAL_MEETING_SETTING_VALUE>(GLOBAL_MEETING_SETTING_VALUE.OFF);
  const [autoCheckInActive, setAutoCheckInActive] = useState<GLOBAL_MEETING_SETTING_VALUE>(
    GLOBAL_MEETING_SETTING_VALUE.OFF
  );
  const [noShowWarningSoundAlertActive, setNoShowWarningSoundAlertActive] = useState<GLOBAL_MEETING_SETTING_VALUE>(
    GLOBAL_MEETING_SETTING_VALUE.OFF
  );
  const [autoCheckOutActive, setAutoCheckOutActive] = useState<GLOBAL_MEETING_SETTING_VALUE>(
    GLOBAL_MEETING_SETTING_VALUE.OFF
  );
  const [autoCheckOutDetectionTime, setAutoCheckOutDetectionTime] = useState<number>(10);
  const [autoCheckOutSoundAlertActive, setAutoCheckOutSoundAlertActive] = useState<GLOBAL_MEETING_SETTING_VALUE>(
    GLOBAL_MEETING_SETTING_VALUE.OFF
  );
  const [meetingFinishAlertActive, setMeetingFinishAlertActive] = useState<GLOBAL_MEETING_SETTING_VALUE>(
    GLOBAL_MEETING_SETTING_VALUE.OFF
  );
  const [meetingFinishAlertTime, setMeetingFinishAlertTime] = useState<number>(10);
  const [meetingFinishSoundAlertActive, setMeetingFinishSoundAlertActive] = useState<GLOBAL_MEETING_SETTING_VALUE>(
    GLOBAL_MEETING_SETTING_VALUE.OFF
  );

  //* 자동 체크인 기능 활성화 여부 변경 (일괄)
  const toggleAutoCheckIn = async (isAutoCheckInEnabled: GLOBAL_MEETING_SETTING_VALUE) => {
    const res = await updateTabletAutoCheckIn(isAutoCheckInEnabled);
    if (res.success) {
      showToast(res.message, "success");
      setAutoCheckInActive(isAutoCheckInEnabled);
    } else {
      showToast(res.message, "error");
    }
  };

  //*노쇼 경고 알림 활성화 여부 변경 (일괄)
  const toggleNoShowWarningSoundAlert = async (isNoShowWarningSoundAlertEnabled: GLOBAL_MEETING_SETTING_VALUE) => {
    if (!hasMeetingRoomUsedTablet) {
      showToast("태블릿 정책은 최소 1개 이상의 회의실이 태블릿을 사용 중일 경우에만 사용 가능합니다.", "warning");
      return;
    }
    const res = await updateNoShowWarningSoundAlert(isNoShowWarningSoundAlertEnabled);
    if (res.success) {
      showToast(res.message, "success");
      setNoShowWarningSoundAlertActive(isNoShowWarningSoundAlertEnabled);
    } else {
      showToast(res.message, "error");
    }
  };

  //* 자동 체크아웃 기능 활성화 여부 변경 (일괄)
  const toggleAutoCheckOut = async (isAutoCheckOutEnabled: GLOBAL_MEETING_SETTING_VALUE) => {
    const res = await updateAutoCheckOut(isAutoCheckOutEnabled);
    if (res.success) {
      showToast(res.message, "success");
      setAutoCheckOutActive(isAutoCheckOutEnabled);
    } else {
      showToast(res.message, "error");
    }
  };

  const selectAutoCheckOutDetectionTime = async (time: number) => {
    const res = await updateAutoCheckOutDetectionTime(time);
    if (res.success) {
      setAutoCheckOutDetectionTime(time);
      showToast(res.message, "success");
    } else {
      showToast(res.message, "error");
    }
  };

  //* 자동 체크아웃 소리 알림 활성화 여부 변경 (일괄)
  const toggleAutoCheckOutSoundAlert = async (iAutoCheckOutSoundAlertEnabled: GLOBAL_MEETING_SETTING_VALUE) => {
    const res = await updateAutoCheckOutSoundAlert(iAutoCheckOutSoundAlertEnabled);
    if (res.success) {
      showToast(res.message, "success");
      setAutoCheckOutSoundAlertActive(iAutoCheckOutSoundAlertEnabled);
    } else {
      showToast(res.message, "error");
    }
  };

  //* 회의 종료 소리 알림 활성화 여부 변경 (일괄)
  const toggleMeetingFinishAlert = async (isMeetingFinishAlertEnabled: GLOBAL_MEETING_SETTING_VALUE) => {
    const res = await updateMeetingFinishAlert(isMeetingFinishAlertEnabled);
    if (res.success) {
      showToast(res.message, "success");
      setMeetingFinishAlertActive(isMeetingFinishAlertEnabled);
    } else {
      showToast(res.message, "error");
    }
  };

  //* 회의 종료 알림 시간 설정 (일괄)
  const selectMeetingFinishAlertTime = async (time: number) => {
    const res = await updateMeetingFinishAlertTime(time);
    if (res.success) {
      setMeetingFinishAlertTime(time);
      showToast(res.message, "success");
    } else {
      showToast(res.message, "error");
    }
  };

  //* 회의 종료 소리 알림 활성화 여부 변경 (일괄)
  const toggleMeetingFinishSoundAlert = async (isMeetingFinishSoundAlertEnabled: GLOBAL_MEETING_SETTING_VALUE) => {
    const res = await updateMeetingFinishSoundAlert(isMeetingFinishSoundAlertEnabled);
    if (res.success) {
      showToast(res.message, "success");
      setMeetingFinishSoundAlertActive(isMeetingFinishSoundAlertEnabled);
    } else {
      showToast(res.message, "error");
    }
  };

  const setTabletPolicyData = async () => {
    const res = await getTabletPolicy();
    if (res.success && res.data) {
      setNoshowEnabled(res.data.isNoShowEnabled);
      setAutoCheckInActive(res.data.isAutoCheckInEnabled);
      setNoShowWarningSoundAlertActive(res.data.isNoShowSoundAlertEnabled);
      setAutoCheckOutActive(res.data.isAutoCheckOutEnabled);
      setAutoCheckOutDetectionTime(res.data.autoCheckOutDetectionTime);
      setAutoCheckOutSoundAlertActive(res.data.isAutoCheckOutSoundAlertEnabled);
      setMeetingFinishAlertActive(res.data.isFinishNotiEnabled);
      setMeetingFinishAlertTime(res.data.finishNotiTime);
      setMeetingFinishSoundAlertActive(res.data.isFinishNotiSoundAlertEnabled);
      setHasMeetingRoomUsedTablet(res.data.hasMeetingRoomUsedTablet);
    }
  };

  useEffect(() => {
    setTabletPolicyData();
  }, []);

  return (
    <Container style={{ minWidth: 1140, boxSizing: "border-box" }}>
      <TitleContainer>
        <UI.Typography type="title03" fontStyle="bold" color="var(--alias-color-label-neutral)">
          태블릿 정책 설정
        </UI.Typography>
        <UI.Tag label="내부 태블릿 전용" color="blue" icon="tablet" showIcon />
      </TitleContainer>
      <SettingContainer>
        <CheckInPolicySetting
          hasMeetingRoomUsedTablet={hasMeetingRoomUsedTablet}
          noShowEnabled={noShowEnabled}
          autoCheckInActive={autoCheckInActive}
          onChangeAutoCheckInActive={toggleAutoCheckIn}
          noShowWarningSoundAlertActive={noShowWarningSoundAlertActive}
          onChangeNoShowWarningSoundAlert={toggleNoShowWarningSoundAlert}
          onUpdate={setTabletPolicyData}
        />
        <CheckOutPolicySetting
          onChangeAutoCheckOutActive={toggleAutoCheckOut}
          hasMeetingRoomUsedTablet={hasMeetingRoomUsedTablet}
          autoCheckOutActive={autoCheckOutActive}
          autoCheckOutDetectionTime={autoCheckOutDetectionTime}
          autoCheckOutSoundAlertActive={autoCheckOutSoundAlertActive}
          onChangeAutoCheckOutDetectionTime={selectAutoCheckOutDetectionTime}
          onChangeAutoCheckOutSoundAlert={toggleAutoCheckOutSoundAlert}
          onUpdate={setTabletPolicyData}
        />
        <PreMeetingEndAlertSetting
          hasMeetingRoomUsedTablet={hasMeetingRoomUsedTablet}
          meetingFinishAlertActive={meetingFinishAlertActive}
          meetingFinishAlertTime={meetingFinishAlertTime}
          meetingFinishSoundAlertActive={meetingFinishSoundAlertActive}
          onChangeMeetingFinishAlert={toggleMeetingFinishAlert}
          onChangeMeetingFinishAlertTime={selectMeetingFinishAlertTime}
          onChangeMeetingFinishSoundAlert={toggleMeetingFinishSoundAlert}
          onUpdate={setTabletPolicyData}
        />
      </SettingContainer>
    </Container>
  );
};

export default SettingTabletAlarm;
